/**
 * 布局纯函数。编辑器与运行时共用，不依赖任何渲染框架。
 * 规则见 docs/ARCHITECTURE.md 第 9 节。
 */
import { CANVAS_HEIGHT, CANVAS_WIDTH, MAX_VIEWPORT_WIDTH, type Layer, type Page, type PageMode } from '@h5/schema';

export interface Viewport {
  /** 设计像素 → 屏幕像素 的缩放比 */
  scale: number;
  /** 实际参与渲染的屏幕宽度（px，桌面端会被 MAX_VIEWPORT_WIDTH 截断） */
  width: number;
  /** 屏幕高度（px） */
  height: number;
  /** 屏幕高度换算成的设计像素高度 */
  designHeight: number;
  /** designHeight - CANVAS_HEIGHT，屏幕比设计稿"多出"的设计像素；短屏为负 */
  diff: number;
}

export function computeScale(viewportWidth: number): number {
  const w = Math.max(1, Math.min(viewportWidth, MAX_VIEWPORT_WIDTH));
  return w / CANVAS_WIDTH;
}

export function computeViewport(size: { vw: number; vh: number }): Viewport {
  const scale = computeScale(size.vw);
  const width = Math.min(size.vw, MAX_VIEWPORT_WIDTH);
  const height = Math.max(1, size.vh);
  const designHeight = height / scale;
  return { scale, width, height, designHeight, diff: designHeight - CANVAS_HEIGHT };
}

/** fixed 页按 anchor 做纵向补偿的偏移量（设计像素）。long 页不补偿。 */
export function anchorOffset(anchor: Layer['anchor'], viewport: Viewport, mode: PageMode = 'fixed'): number {
  if (mode === 'long') return 0;
  switch (anchor) {
    case 'center':
      return viewport.diff / 2;
    case 'bottom':
      return viewport.diff;
    default:
      return 0;
  }
}

export interface LayerBoxStyle {
  left: number;
  top: number;
  width: number;
  height: number;
  transform: string;
  opacity: number;
  zIndex: number;
}

/**
 * 图层在页面容器内的样式（单位：设计像素）。
 * 页面容器自身已经 transform: scale(viewport.scale)，所以这里不乘 scale。
 */
export function layerToStyle(layer: Layer, viewport: Viewport, mode: PageMode = 'fixed'): LayerBoxStyle {
  const { rect } = layer;
  return {
    left: rect.x,
    top: rect.y + anchorOffset(layer.anchor, viewport, mode),
    width: rect.w,
    height: rect.h,
    transform: rect.rotate ? `rotate(${rect.rotate}deg)` : 'none',
    opacity: layer.style.opacity,
    zIndex: layer.style.zIndex,
  };
}

/** 页面容器（未缩放前）的设计像素尺寸。 */
export function pageDesignSize(page: Pick<Page, 'mode' | 'height'>, viewport: Viewport): { width: number; height: number } {
  if (page.mode === 'long') {
    return { width: CANVAS_WIDTH, height: Math.max(page.height, CANVAS_HEIGHT) };
  }
  return { width: CANVAS_WIDTH, height: Math.max(viewport.designHeight, CANVAS_HEIGHT) };
}

/** 页面容器的 CSS（scale 后正好铺满 viewport.width × viewport.height，或 long 页的内容高度）。 */
export function pageContainerStyle(page: Pick<Page, 'mode' | 'height'>, viewport: Viewport): Record<string, string> {
  const size = pageDesignSize(page, viewport);
  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    transform: `scale(${viewport.scale})`,
    transformOrigin: '0 0',
  };
}
